import React from 'react';
import { Link } from 'react-router-dom';
import { TeacherApplicationState } from '../api/teacher-applications';

/**
 * Explains where the teacher's application stands, and — when publishing is
 * locked — says so plainly instead of leaving them to discover it by having a
 * publish attempt rejected.
 *
 * The server-side gate is `teacherProfile.verificationStatus === 'VERIFIED'`,
 * which a listener flips when an admin approves the application. This component
 * narrates the *application*; `useTeacherVerification` reads the field the gate
 * actually enforces. They agree in the ordinary case and can disagree when
 * activation fails after an approval — which is precisely the case worth
 * showing rather than papering over.
 */

export type ApplicationStateOrMissing = TeacherApplicationState | 'MISSING';

/**
 * Whether the *application* has been approved.
 *
 * This is not the publish gate, though it was used as one while
 * `teacherProfile.verificationStatus` was unavailable to the frontend (S11.1).
 * Prefer `useTeacherVerification`; this remains as the fallback for when the
 * profile cannot be read.
 */
export function applicationApproved(state: ApplicationStateOrMissing): boolean {
  return state === 'APPROVED';
}

interface Tone {
  bg: string;
  border: string;
  fg: string;
}

const TONES: Record<'info' | 'warn' | 'error' | 'success', Tone> = {
  info: { bg: '#eaf2fb', border: '#4a90e2', fg: '#1c4f80' },
  warn: { bg: '#fff8e6', border: '#f5a623', fg: '#7a4d06' },
  error: { bg: '#ffe9e9', border: '#cb4242', fg: '#8f2323' },
  success: { bg: '#edf8e5', border: '#5ba617', fg: '#3f7410' },
};

interface Copy {
  tone: keyof typeof TONES;
  title: string;
  body: string;
  ctaLabel?: string;
  ctaTo?: string;
}

function copyFor(state: ApplicationStateOrMissing, reason?: string): Copy | null {
  switch (state) {
    case 'MISSING':
      return {
        tone: 'info',
        title: 'Completa tu solicitud para empezar a enseñar',
        body: 'Necesitamos verificar tu perfil antes de que puedas publicar cursos. Toma unos minutos.',
        ctaLabel: 'Iniciar solicitud',
        ctaTo: '/teacher-onboarding',
      };
    case 'DRAFT':
      return {
        tone: 'warn',
        title: 'Tu solicitud está incompleta',
        body: 'Guardaste tu solicitud pero aún no la enviaste. No podrás publicar cursos hasta que la completes y sea aprobada.',
        ctaLabel: 'Continuar solicitud',
        ctaTo: '/teacher-onboarding',
      };
    case 'SUBMITTED':
    case 'UNDER_REVIEW':
      return {
        tone: 'info',
        title: 'Tu solicitud está en revisión',
        body: 'Puedes crear cursos como borrador mientras tanto. Podrás publicarlos en cuanto aprobemos tu solicitud.',
        ctaLabel: 'Ver mi solicitud',
        ctaTo: '/teacher-onboarding',
      };
    case 'NEEDS_RESUBMIT':
      return {
        tone: 'warn',
        title: 'Tu solicitud necesita cambios',
        body: reason
          ? `Motivo: ${reason}`
          : 'Revisa los comentarios del equipo y vuelve a enviarla para poder publicar cursos.',
        ctaLabel: 'Corregir y reenviar',
        ctaTo: '/teacher-onboarding',
      };
    case 'REJECTED':
      return {
        tone: 'error',
        title: 'Tu solicitud fue rechazada',
        body: reason
          ? `Motivo: ${reason}. Escríbenos si crees que se trata de un error.`
          : 'Escríbenos si crees que se trata de un error.',
        ctaLabel: 'Ver detalles',
        ctaTo: '/teacher-onboarding',
      };
    case 'APPROVED':
      // Nothing is blocked — say nothing rather than add noise.
      return null;
    default:
      return null;
  }
}

interface Props {
  state: ApplicationStateOrMissing;
  /** Review feedback from the admin, shown for NEEDS_RESUBMIT / REJECTED. */
  reason?: string;
}

const ApplicationStatusBanner: React.FC<Props> = ({ state, reason }) => {
  const copy = copyFor(state, reason);
  if (!copy) return null;

  const tone = TONES[copy.tone];

  return (
    <div
      role="status"
      style={{
        background: tone.bg,
        border: `1px solid ${tone.border}`,
        borderRadius: 8,
        padding: '14px 16px',
        marginBottom: 20,
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 12,
      }}
    >
      <div style={{ flex: '1 1 320px', minWidth: 0 }}>
        <div style={{ fontWeight: 700, color: tone.fg, marginBottom: 2 }}>{copy.title}</div>
        <div style={{ fontSize: 14, color: tone.fg }}>{copy.body}</div>
      </div>
      {copy.ctaLabel && copy.ctaTo && (
        <Link
          to={copy.ctaTo}
          className="btn btn-primary btn-sm"
          style={{ textDecoration: 'none', whiteSpace: 'nowrap' }}
        >
          {copy.ctaLabel}
        </Link>
      )}
    </div>
  );
};

export default ApplicationStatusBanner;
